import { useQuery } from '@tanstack/react-query';

import { kinds, type EnvKey } from '@/app/site/scope';
import { constantsList } from '@/shared/api/generated/constants/constants';
import { contentFieldTypes } from '@/shared/api/generated/content/content';
import type { ConstantBody } from '@/shared/api/generated/model';

export function constantsKey(siteId: string, env: EnvKey) {
  return ['constants', siteId, env] as const;
}

export function useConstants(siteId: string, env: EnvKey, enabled = true) {
  return useQuery({
    queryKey: constantsKey(siteId, env),
    enabled,
    queryFn: async () => {
      const response = await constantsList(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос констант');
      }

      return response.data.items ?? [];
    },
  });
}

export function useFieldTypes() {
  return useQuery({
    queryKey: ['field-types'],
    staleTime: Infinity,
    queryFn: async () => {
      const response = await contentFieldTypes();

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос видов полей');
      }

      return response.data.items ?? [];
    },
  });
}

/** Значение константы словами — для плитки и списка. */
export function valueText(constant: ConstantBody): string {
  const value = constant.value;

  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (constant.type === 'boolean') {
    return value === true ? 'да' : 'нет';
  }

  if (constant.type === 'image') {
    return 'картинка';
  }

  if (constant.type === 'file') {
    return 'файл';
  }

  return typeof value === 'string' || typeof value === 'number' ? String(value) : '—';
}

/** Константы по группам — в порядке первого появления группы. */
export function bySection(items: ConstantBody[]): [string, ConstantBody[]][] {
  const groups = new Map<string, ConstantBody[]>();

  for (const item of items) {
    groups.set(item.section, [...(groups.get(item.section) ?? []), item]);
  }

  return [...groups.entries()];
}
